import React, { useEffect, useState } from 'react';
import {
  Button, Form, Modal, Spinner, Table,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import { CrudStatus as AvtechApiStatus } from '../../../../utility/types/CrudStatus';
import {
  fetchSingleDevice,
  selectSensorUpdateStatus,
  selectSingleDeviceErrors,
  updateSensor,
} from '../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import { getDefaultAdapterPortLabel, parseChannelKey } from '../../../../utility/channelUtils';

const AdapterSettingsModal = ({
  show, handleClose, sensorLabel, isHidden, onToggleHidden, adapterChannelGroups,
}) => {
  const dispatch = useDispatch();
  const [label, setLabel] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const [tempIsHidden, setTempIsHidden] = useState(isHidden);
  const saveStatus = useSelector(selectSensorUpdateStatus);
  const validationErrors = useSelector(selectSingleDeviceErrors);
  const adapterBleChannelGroup = adapterChannelGroups.find(
    (cg) => parseChannelKey(cg.channelKey).channelGroupType === ChannelGroupType.BLUETOOTH_LE.id,
  );

  useEffect(() => {
    if (!isEdited) {
      setLabel(sensorLabel);
    }
  }, [sensorLabel, isEdited]);

  useEffect(() => {
    if (!show) {
      setIsEdited(false);
      setTempIsHidden(isHidden);
    }
  }, [show]);

  const handleLabelChange = (event) => {
    setIsEdited(true);
    setLabel(event.target.value);
  };

  const handleSubmit = async () => {
    try {
      const updatedChannels = adapterChannelGroups.map((channel) => ({
        channelKey: channel.channelKey,
        adjust: channel.adjust || 0,
        // conversion: channelInstance.conversion || { type: 'NONE' },
      }));
      const payload = {
        channelKey: adapterBleChannelGroup.channelKey, // Get channelKey from first channel group
        channels: updatedChannels,
        label, // Only updating the label
      };
      await dispatch(updateSensor(payload)).unwrap();
      await dispatch(fetchSingleDevice({})).unwrap();
      if (tempIsHidden !== isHidden) {
        onToggleHidden();
      }
      handleClose();
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save settings:', error);
      throw new Error(error.message || 'Failed to save sensor settings');
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Adapter Settings</Modal.Title>
      </Modal.Header>

      <Button
        variant="link"
        className="text-muted text-left ml-3 mt-2 mb-2 p-0"
        onClick={() => setTempIsHidden(!tempIsHidden)}
      >
        <FontAwesomeIcon
          icon={tempIsHidden ? faEye : faEyeSlash}
          className="mr-2"
        />
        {tempIsHidden ? 'Show adapter' : 'Hide adapter'}
      </Button>

      <Modal.Body>
        <Form>
          <Form.Group controlId="sensorLabel">
            <Form.Control
              type="text"
              value={label}
              onChange={handleLabelChange}
              isInvalid={!!validationErrors.label}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.label}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Text className="text-muted">
            <small className="ms-2">
              Max chars 50
            </small>
          </Form.Text>

          <Table striped className="table">
            <thead style={{ backgroundColor: 'rgb(88, 88, 88)', color: 'white', borderBottom: 'none' }}>
              <tr>
                <th>Channel</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {adapterChannelGroups
              // eslint-disable-next-line max-len
                .filter((cg) => {
                  const parsedChannelGroupKey = parseChannelKey(cg.channelKey);
                  return parsedChannelGroupKey.channelGroupType !== ChannelGroupType.BLUETOOTH_LE.id
                      && parsedChannelGroupKey.channelGroupIndex !== 0;
                }).map((group) => {
                  const parsedKey = parseChannelKey(group.channelKey);
                  const channelGroupInstance = ChannelService.getChannelGroupTypeByValueId(
                    parsedKey.channelGroupType,
                  );
                  return (
                    <tr key={group.channelKey}>
                      <td>
                        {getDefaultAdapterPortLabel(parseChannelKey(
                          group.channelKey,
                        ).channelGroupIndex)}
                      </td>
                      <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        <div className="d-flex align-items-center">
                          <Form.Control
                            type="text"
                            className="w-75"
                            value={channelGroupInstance.displayName}
                            readOnly
                            disabled
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saveStatus === AvtechApiStatus.PENDING}>
          {saveStatus === AvtechApiStatus.PENDING
            ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AdapterSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  sensorLabel: PropTypes.string.isRequired,
  isHidden: PropTypes.bool.isRequired,
  onToggleHidden: PropTypes.func.isRequired,
  adapterChannelGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default AdapterSettingsModal;
